import {
    concatActionType,
    concatActionTypeEx,
    wrapActionTypes,
} from "../reduxActionTypesUtils";
import {
    Action,
    AnyAction,
    combineReducers,
    Func1,
    Func2,
    Reducer,
    ReducersMapObject,
} from "redux";

export * from "./typeExtandableReducer";
export * from "./combineReducers/combineReducers";
export * from "./combineReducers/combineReducersImmer";

export function reducerWrapper<AT, A extends any[], R>(
    actionTypes: AT,
    fn: (arg0: AT, ...args: A) => R,
    prefix: string
): (...args: A) => R {
    const at = wrapActionTypes(actionTypes, prefix);
    return fn.bind(null, at);
}

export function reducerPrefixWrapper<AT, A extends any[], R>(
    actionTypes: AT,
    fn: (arg0: AT, ...args: A) => R
) {
    return (prefix: string) => reducerWrapper(actionTypes, fn, prefix);
}

export const emptyReducer = (s = null, a) => s;

export function buildReducerByArray(object: any, reducer) {
    const reducersList = {};
    const keys = Object.keys(object);
    if (keys.length < 1) return emptyReducer;

    for (let i of keys) reducersList[i] = reducer;

    return combineReducers(reducersList);
}

export function buildSubPropertyReducer(key: string, reducer) {
    return (state, action) => {
        const newState = reducer(state[key], action);
        return state[key] == newState ? state : { ...state, [key]: newState };
    };
}

export function buildSubPropertyReducerByArray(
    key: string,
    object: any,
    reducer
) {
    const listReducer = buildReducerByArray(object, reducer);
    return buildSubPropertyReducer(key, listReducer);
}

export type ExtendedReducersMapObject<S, P> = {
    [K in keyof Omit<S, keyof P>]: (state: S[K], action: any, part: P) => S[K];
};

const wrapStateBag = (reducer, bag) => (state, action) =>
    reducer(state, action, bag.firstPartState);

export function combineReducerWithSelfExtendingSubReducers<TPart>(
    keys: (keyof TPart)[]
) {
    return {
        for: function x<S extends TPart>(
            first: ReducersMapObject<TPart, any>,
            second: ExtendedReducersMapObject<S, TPart>
        ) {
            const firstPart = {} as ReducersMapObject<S, any>;
            const secondPart = {} as ReducersMapObject<S, any>;

            for (let key of keys) {
                firstPart[key] = first[key] as any;
                secondPart[key] = emptyReducer;
            }

            const bag = { firstPartState: null };
            for (let key of Object.keys(second)) {
                firstPart[key] = emptyReducer;
                secondPart[key] = wrapStateBag(second[key], bag);
            }

            const firstPartReducer = combineReducers(firstPart);
            const secondPartReducer = combineReducers(secondPart);

            return function (state: S, action) {
                const firstPart = firstPartReducer(state, action);

                bag.firstPartState = {};
                for (let key of keys) bag.firstPartState[key] = firstPart[key];

                const firstPartState =
                    firstPart === state
                        ? state
                        : { ...state, ...bag.firstPartState };
                return secondPartReducer(firstPartState, action) as S;
            };
        },
    };
}

export interface ISourcedState {
    "@source": string;
}

export type TSouredStateEx<T> = T & ISourcedState;

export function filterActionsBySource<
    S extends ISourcedState,
    A extends AnyAction = AnyAction
>(
    reducer: Reducer<Omit<S, "@source">, A>,
    defaultValue?: Omit<S, "@source">
): Func1<string, Reducer<S, A>> {
    return source => {
        const extendedDefValue = { ...defaultValue, "@source": source };

        return (s, a) =>
            ((s && a.source === s["@source"]) || a.source == "@any"
                ? reducer(s || extendedDefValue, a)
                : s || extendedDefValue) as any;
    };
}

export type ReducersBuildersMapObject<S, C> = {
    [K in keyof S]: (config: C) => Reducer<S[K]>;
};

export function combineReducersBuilders<S, C = any>(
    reducerBuilders: ReducersBuildersMapObject<S, C>
): Func1<C, Reducer<S>> {
    return (config, fn = combineReducers) => {
        const reducers = {} as ReducersMapObject<S>;

        Object.keys(reducerBuilders).map(
            k => (reducers[k] = reducerBuilders[k](config))
        );

        return fn(reducers);
    };
}

export function combineReducersBuildersEx<S, C = any>(
    reducerBuilders: ReducersBuildersMapObject<S, C>
): Func2<C, Func1<ReducersMapObject<S, any>, Reducer<S>>, Reducer<S>> {
    return (combineReducersBuilders as any)(reducerBuilders);
}

export function fillReducerBuilders<TParams, T, A extends Action = AnyAction>(
    params: TParams,
    reducerBuilder: Func1<TParams, Reducer<T, A>>
): Reducer<T, A> {
    return reducerBuilder(params);
}

export const nullSelector = <T>() => () => null as T;
export const emptyObjectSelector = <T>() => () => ({} as T);

export function addReducerHandlersKeysPostfix(obj, postfix: string) {
    const p: any = {};
    for (let key in obj) p[concatActionType(key, postfix)] = obj[key];
    return p;
}

export function addReducerHandlersKeysModifier<T>(
    obj: IReducerHandlers<T>,
    modifier: string
): IReducerHandlers<T> {
    const p: any = {};
    for (let key in obj) p[fillActionTypeModifier(key, modifier)] = obj[key];
    return p;
}

export const fillActionTypeModifier = (actionType: string, modifier: string) =>
    actionType.replace("{modifier}", modifier);

export function addReducerHandlersKeysModifierEx<T>(
    obj: IReducerHandlers<T>,
    modifier: string
): IReducerHandlers<T> {
    const p: any = {};
    for (let key in obj) p[concatActionTypeEx(key, modifier)] = obj[key];
    return p;
}

export interface IReducerHandlers<TState> {
    [key: string]: Reducer<TState>;
}

export function buildReducerByHandlers<TState>(
    handlers: IReducerHandlers<TState>,
    defaultState: TState = null
) {
    return (state: TState, action) => {
        const handler = handlers[action.type];
        return handler
            ? handler(state, action)
            : state === undefined
            ? defaultState
            : state;
    };
}
